import { useEffect, useState, type ComponentProps } from 'react'
import { formatQuantity, parseQuantity } from '@hk/core'
import { useLocale } from '@/lib/i18n/locale'
import { cn } from '@/lib/utils'

/* ============================================================================
   A NUMBER SOMEBODY TYPES.  CQ-139.

   ── WHY IT IS NOT `<input type="number">` ────────────────────────────────────────────
   **BECAUSE A BROWSER'S NUMBER FIELD REFUSES ARABIC DIGITS AT THE KEYBOARD.** A person
   typing ٤ into `type="number"` does not get 4 and does not get an error — the value
   simply never arrives, and the box stays empty while they press the key again. Every
   number on every screen in this product is DISPLAYED in Eastern Arabic-Indic numerals for
   an Arabic reader; this was the one direction that did not work, which is the worst
   possible arrangement: the product shows you ٤ and will not let you type it back.

   The client's answer to CQ-139 on 17 September was **"accept both, display Eastern"**, and
   this is the "accept both" half. The handset has had it since Sprint 22, because a
   `TextInput` there was always plain text and `parseQuantity` was always in the way; the
   web had a native control doing the work and doing it wrong.

   ── HOW ────────────────────────────────────────────────────────────────────────────
   A TEXT input with `inputMode="numeric"`, so a phone still shows a keypad, and every
   keystroke folded through `parseQuantity` — the SAME function the handset uses, which
   accepts ٠-٩, the Persian/Urdu ۰-۹, the Arabic decimal mark ٫, the Arabic thousands mark
   ٬ and the direction marks a copy-and-paste leaves behind.

   ── AND IT DISPLAYS EASTERN, WHICH IS THE OTHER HALF OF THE ANSWER ──────────────────
   The client said **"accept both, display Eastern"**, and the first version of this
   component did only the first half: it accepted ٤ and then rendered the stored value back
   as `4`. On a screen where every other figure is ٤, one Western digit inside a box is the
   only number in the wrong script — which is defect #84's shape, and it was caught by
   looking at the rendered Arabic screen rather than by a test.

   **WHAT SOMEBODY IS TYPING IS LEFT EXACTLY AS THEY TYPED IT.** Only a value arriving from
   the caller is formatted; reformatting under a cursor is how a form loses somebody's
   trust.

   ── THE VALUE IS HELD AS TEXT WHILE IT IS BEING TYPED ───────────────────────────────
   **AND THAT IS THE WHOLE DESIGN**, borrowed from the handset's `Stepper`. A numeric state
   cannot represent "they have cleared the box and not typed anything yet": it has to pick
   0 or NaN, and 0 is a quantity somebody meant while an empty box is not. The caller is
   told `null` for an empty box and a number otherwise, and never has to guess which.
   ========================================================================= */

export interface NumberFieldProps
  extends Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type'> {
  /** The number, or null for an empty box. */
  value: number | null
  /** Null when the box is empty. Called on every keystroke that parses. */
  onChange: (value: number | null) => void
  /** Called when the field is left, with the last parsed value. */
  onCommit?: (value: number | null) => void
  min?: number
  max?: number
}

export function NumberField({
  value, onChange, onCommit, min, max, className, ...props
}: NumberFieldProps) {
  const { locale } = useLocale()

  /* IN THE READER'S OWN NUMERALS. `formatQuantity` is the same function every figure on
     every screen goes through, so a box holding four sits beside a heading saying four and
     they are written the same way. */
  const shown = (n: number | null) => (n === null ? '' : formatQuantity(n, locale))

  /* THE TYPED TEXT, which is not the same thing as the number. `٤` and `4` are the same
     value and different strings, and re-rendering somebody's ٤ as 4 under their cursor is
     the sort of thing that makes people distrust a form. */
  const [text, setText] = useState(() => shown(value))

  /* WHEN THE CALLER CHANGES THE VALUE UNDERNEATH — a save that came back, a row reset —
     the box follows. Not while the two already agree, or every keystroke would fight it.
     **The locale is a dependency too**: switching language mid-form must rewrite a figure
     nobody is currently typing into. */
  useEffect(() => {
    setText((current) => (parseQuantity(current) === value ? current : shown(value)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, locale])

  const read = (typed: string): number | null => {
    if (typed.trim() === '') return null
    const parsed = parseQuantity(typed)
    if (parsed === null) return null
    if (min !== undefined && parsed < min) return null
    if (max !== undefined && parsed > max) return null
    return parsed
  }

  return (
    <input
      {...props}
      type="text"
      inputMode="numeric"
      /* **NOT `pattern="[0-9]*"`.** That is the idiom everybody copies, and on a field
         that deliberately accepts ٤ it would mark every Arabic entry invalid. */
      value={text}
      onChange={(e) => {
        setText(e.target.value)
        onChange(read(e.target.value))
      }}
      onBlur={(e) => {
        onCommit?.(read(e.target.value))
        props.onBlur?.(e)
      }}
      className={cn(
        'h-control w-full rounded-hk border border-input-border bg-input px-3',
        'text-sm text-foreground font-sans tabular-nums outline-none placeholder:text-muted-fg',
        'focus:border-[#7dd3fc] focus:ring-3 focus:ring-[color-mix(in_oklab,#7dd3fc_35%,transparent)]',
        'disabled:opacity-60 disabled:cursor-not-allowed',
        className,
      )}
    />
  )
}
